"use client";

import type { MarkdownHeading } from "@/lib/content/markdown";
import { typographyStyles } from "@/lib/theme/tokens";
import { cn } from "@/lib/utils";

type MarkdownOutlineProps = {
  headings: MarkdownHeading[];
  activeId?: string;
  onNavigate?: (id: string) => void;
  className?: string;
};

const OUTLINE_INDENT_PX: Record<MarkdownHeading["depth"], number> = {
  1: 0,
  2: 20,
  3: 32,
  4: 44,
  5: 56,
  6: 68,
};

export function MarkdownOutline({ headings, activeId, onNavigate, className }: MarkdownOutlineProps) {
  if (!headings.length) {
    return null;
  }

  const handleNavigate = (id: string, event: React.MouseEvent<HTMLAnchorElement>) => {
    if (onNavigate) {
      event.preventDefault();
      onNavigate(id);
    }
  };

  return (
    <nav
      aria-label="Markdown outline"
      className={cn(
        "rounded-[var(--radius-xl)] border border-border/70 bg-background/70 p-4 shadow-[var(--shadow-xs)]",
        className,
      )}
    >
      <p style={typographyStyles.detail} className="uppercase tracking-[0.32em] text-muted-foreground/80">
        Outline
      </p>
      <ul className="mt-3 space-y-1">
        {headings.map((heading) => {
          const indent = OUTLINE_INDENT_PX[heading.depth] ?? 0;
          const anchorStyle = indent ? { paddingLeft: `${indent}px` } : undefined;

          return (
            <li key={heading.id}>
              <a
                href={`#${heading.id}`}
                onClick={(event) => handleNavigate(heading.id, event)}
                data-active={heading.id === activeId || undefined}
                className={cn(
                  "flex items-start gap-3 rounded-[var(--radius-lg)] border border-transparent px-3 py-2 text-left text-sm transition",
                  "hover:border-border/70 hover:bg-muted/30",
                  "data-[active=true]:border-border/70 data-[active=true]:bg-card/70 data-[active=true]:shadow-[var(--shadow-xs)]",
                )}
                style={anchorStyle}
              >
                <span style={typographyStyles.detail} className="text-muted-foreground/70">
                  {heading.depth.toString()}
                </span>
                <span style={typographyStyles["body-medium"]} className="leading-snug text-foreground">
                  {heading.text}
                </span>
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
